
import { Options, prop, Vue } from "vue-class-component";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { OptionsClass } from "@/domain/entities/MainApp";
import Loading from "@/app/ui/components/loading/index.vue";
import {
  ERROR_GENERATE_TYPE,
  INTEGRATION_PLATFORMS,
  INTEGRATION_PLATFORM_ENGLISH_COPY
} from "../constant";
import { IntegrationApi } from "@/domain/entities/Integration";
import { MiddlewareController } from "@/app/ui/controllers/MiddlewareController";
import { AddGenerateTokenRequest } from "@/data/payload/api/MiddlewareApiRequest";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";

class Props {
  modelValue = prop<boolean>({ default: false, type: Boolean });
  platform = prop<OptionsClass>({
    default: () => new OptionsClass(),
    type: Object
  });
}

@Options({
  components: {
    Loading
  },
  emits: ["update:modelValue", "refresh"],
  watch: {
    show(val: boolean) {
      if (val) {
        this.initModalData();
      }
    }
  }
})
class ModalAddNewToken extends Vue.with(Props) {
  form = new IntegrationApi();

  get modalTitle() {
    return "Add new token";
  }

  get platformPlaceholder() {
    return "Select a platform";
  }

  get storeNameLabel() {
    return "Store name";
  }

  get storeNamePlaceholder() {
    return "Enter store name";
  }

  get domainPlaceholder() {
    return "Enter domain (e.g. domain.com)";
  }

  get generateTokenLabel() {
    return "Generate Token";
  }

  get show() {
    return this.modelValue;
  }

  set show(val: boolean) {
    this.$emit("update:modelValue", val);
  }

  onReset() {
    this.show = false;
  }

  initModalData() {
    this.form = new IntegrationApi({
      platform: this.platform
    });
    this.error = new IntegrationApi();
  }

  get platformOptions() {
    return INTEGRATION_PLATFORMS.map((platform) => ({
      ...platform,
      ...INTEGRATION_PLATFORM_ENGLISH_COPY[platform.value]
    }));
  }

  get disabled() {
    const { platform, appName, domain } = this.form;
    return [
      !(platform as OptionsClass)?.name,
      !appName,
      !domain,
      this.error.appName,
      this.error.domain
    ].some(Boolean);
  }

  error = new IntegrationApi();
  async onProcess() {
    try {
      MainAppController.closeMessageModal();
      MainAppController.closeErrorMessage();
      MainAppController.showLoading("Processing");

      await MiddlewareController.addGenerateToken(
        new AddGenerateTokenRequest(this.form)
      );

      this.onCreateResponse();
    } catch (error) {
      const message = parsingErrorResponse(error).message;
      const messages = message
        .split("|")
        .map((item: string) => item.trim())
        .filter(Boolean);

      for (const item of messages) {
        const translatedItem = this.translateValidationMessage(item);
        const type =
          ERROR_GENERATE_TYPE[item as keyof typeof ERROR_GENERATE_TYPE];

        if (type) {
          this.error[type] = translatedItem;
        }
      }

      this.onCreateResponse("error");
    } finally {
      MainAppController.closeLoading();
    }
  }

  translateValidationMessage(message: string) {
    const translations: Record<string, string> = {
      "Nama toko sudah digunakan. Coba lainnya":
        "Store name is already use. Please try again",
      "Domain sudah digunakan. Coba lainnya":
        "Domain is already use. Please try again"
    };
    return translations[message] || message;
  }

  onCreateResponse(type: "error" | "success" = "success") {
    MainAppController.setSnackbarPositivity(type === "success");
    MainAppController.setSnackbarMessage(
      type === "success"
        ? "Token added successfully."
        : "Failed to add token. Please try again."
    );
    MainAppController.setSnackbarTimeout(5000);
    MainAppController.setAutoClose(true);
    MainAppController.setSnackbar(true);

    if (type === "success") {
      this.onReset();
      this.$emit("refresh");
    }
  }
}

export default ModalAddNewToken;
